import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, NgZone, AfterViewChecked, ChangeDetectorRef, OnDestroy, OnChanges, SimpleChanges, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AiSearchBarComponent } from '../ai-search-bar/ai-search-bar.component';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AiSettingsService, AIModelProvider } from '../../../services/ai-settings.service';
import { AiApiService } from '../../../services/ai-api.service';
import { NowList, SnApiAuth, SnApiService } from '../../../services/sn-api.service';
import { marked } from 'marked';

export interface ChatMessage {
  role: 'user' | 'model';
  message: string;
  isStreaming?: boolean;
  timestamp: Date;
  avatar?: string;
}

// Audio utility functions (from smartux-demo)
function encode(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function createBlob(data: Float32Array): { data: string; mimeType: string } {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    int16[i] = data[i] * 32768;
  }
  return {
    data: encode(new Uint8Array(int16.buffer)),
    mimeType: 'audio/pcm;rate=16000',
  };
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number
): Promise<AudioBuffer> {
  const buffer = ctx.createBuffer(
    numChannels,
    data.length / 2 / numChannels,
    sampleRate
  );

  const dataInt16 = new Int16Array(data.buffer);
  const l = dataInt16.length;
  const dataFloat32 = new Float32Array(l);
  for (let i = 0; i < l; i++) {
    dataFloat32[i] = dataInt16[i] / 32768.0;
  }

  if (numChannels === 1) {
    buffer.copyToChannel(dataFloat32, 0);
  } else {
    for (let i = 0; i < numChannels; i++) {
      const channel = dataFloat32.filter((_, index) => index % numChannels === i);
      buffer.copyToChannel(channel, i);
    }
  }

  return buffer;
}

@Component({
  selector: 'app-chat-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, AiSearchBarComponent],
  templateUrl: './chat-dialog.component.html',
  styleUrls: ['./chat-dialog.component.scss']
})
export class ChatDialogComponent implements AfterViewChecked, OnDestroy, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() userAvatarSrc: string = 'user-avatars/personas/claire.png'; // Default user avatar
  @Input() userProfile: string = 'claire'; // User profile name for backend credential selection
  @Output() closeDialog = new EventEmitter<void>();

  history: ChatMessage[] = [];
  inputValue: string = '';
  isRecording: boolean = false;
  isThinking: boolean = false;
  isAiSpeaking: boolean = false;
  isMuted: boolean = true; // Default to muted (sound off) - user must click speaker to enable

  // Voice options for Gemini
  geminiVoices: Array<{ name: string; gender: string; description?: string }> = [
    { name: 'Puck', gender: 'Male' },
    { name: 'Charon', gender: 'Male' },
    { name: 'Fenrir', gender: 'Male' },
    { name: 'Orus', gender: 'Male' },
    { name: 'Aoede', gender: 'Female' },
    { name: 'Kore', gender: 'Female' },
    { name: 'Leda', gender: 'Female' },
    { name: 'Zephyr', gender: 'Female' }
  ];

  // Voice options for Azure OpenAI Realtime API
  // See: https://learn.microsoft.com/en-us/azure/ai-foundry/openai/realtime-audio-reference
  azureVoices: Array<{ name: string; gender: string; description?: string }> = [
    { name: 'alloy', gender: 'Female', description: 'Warm and confident' },
    { name: 'ash', gender: 'Male', description: 'Clear and direct' },
    { name: 'ballad', gender: 'Male', description: 'Soft and melodic' },
    { name: 'coral', gender: 'Female', description: 'Warm and curious' },
    { name: 'echo', gender: 'Male', description: 'Smooth and balanced' },
    { name: 'sage', gender: 'Female', description: 'Wise and calm' },
    { name: 'shimmer', gender: 'Female', description: 'Bright and optimistic' },
    { name: 'verse', gender: 'Male', description: 'Versatile and expressive' }
  ];

  selectedGeminiVoice: string = 'Puck';
  selectedAzureVoice: string = 'ash';

  // Get available voices based on current provider
  get availableVoices() {
    return this.modelProvider === 'azure' ? this.azureVoices : this.geminiVoices;
  }

  // Get/set selected voice based on current provider
  get selectedVoice(): string {
    return this.modelProvider === 'azure' ? this.selectedAzureVoice : this.selectedGeminiVoice;
  }

  set selectedVoice(value: string) {
    if (this.modelProvider === 'azure') {
      this.selectedAzureVoice = value;
    } else {
      this.selectedGeminiVoice = value;
    }
  }

  // Get AI avatar based on selected voice gender
  get aiAvatarSrc(): string {
    const voices = this.modelProvider === 'azure' ? this.azureVoices : this.geminiVoices;
    const voice = voices.find(v => v.name === this.selectedVoice);
    return voice?.gender === 'Female' ? 'user-avatars/personas/claire.png' : 'user-avatars/agents/ai-agent.png';
  }

  @ViewChild('chatBody') private chatBody!: ElementRef;
  @ViewChild(AiSearchBarComponent) private chatSearchBar?: AiSearchBarComponent;

  // WebSocket connection to backend proxy (for Gemini)
  private ws: WebSocket | null = null;
  private wsReady: boolean = false;

  // WebRTC connection (for Azure)
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private rtcReady: boolean = false;

  // Web Audio API (exactly like smartux-demo - created immediately)
  private inputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
  private outputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
  private inputNode = this.inputAudioContext.createGain();
  private outputNode = this.outputAudioContext.createGain();
  private nextStartTime = 0;
  private mediaStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private scriptProcessorNode: ScriptProcessorNode | null = null;
  private sources = new Set<AudioBufferSourceNode>();

  // Track streaming messages - separate for user and AI
  private currentUserMessageIndex: number = -1;
  private currentAiMessageIndex: number = -1;
  private currentPhraseStartIndex: number = 0;
  private userMessageBeforeAiSpoke: string = ''; // Store user message when AI starts speaking
  private pendingVoiceChange: string | null = null; // Queue voice change while AI is speaking
  private aiStoppedSpeakingTime: number = 0; // Timestamp when AI stopped speaking (to ignore echo)
  private pendingNavigation: string | null = null; // Queue navigation while AI is speaking
  private hasRawMarkdownForCurrentResponse: boolean = false; // Skip transcription when raw markdown is displayed
  private wasRecordingBeforeAiSpoke: boolean = false; // Track if mic was on before AI started speaking
  private responseCancelled: boolean = false; // Flag to ignore incoming audio after user interrupts
  private awaitingVendorOnboardConfirmation = false;
  private pendingVendorSearchName = '';
  private vendorOnboardRedirectInProgress = false;
  private readonly vendorOnboardingTable = 'u_angular___vendor_onboard_form';
  private readonly parsedMessageCache = new Map<string, SafeHtml>();
  private readonly maxParsedMessageCacheEntries = 200;

  // Web Speech API for transcription
  private recognition: any = null;

  logoSrc = 'logos/brand/riministreet-black.svg';

  // State for dynamic features
  isWideMode: boolean = false; // Controls expanded width for tables

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private aiSettings: AiSettingsService,
    private sanitizer: DomSanitizer,
    private ai: AiApiService,
    private snApi: SnApiService,
    private router: Router
  ) {
    // Connect output node to destination
    this.outputNode.connect(this.outputAudioContext.destination);
    // Set initial gain to 0 since isMuted defaults to true
    this.outputNode.gain.value = 0;

    // Initialize speech recognition for transcription
    this.initSpeechRecognition();

    console.log('[LiveChat] Component initialized');
  }

  // Get the current model provider from settings
  get modelProvider(): AIModelProvider {
    return this.aiSettings.modelProvider;
  }

  /**
   * Toggle mute/unmute for AI voice output.
   * If AI is currently speaking, stop the audio stream entirely.
   * Otherwise, toggle mute for future audio.
   */
  toggleMute(): void {
    // If AI is speaking, stop it entirely (like an interrupt) and mute
    if (this.isAiSpeaking || this.sources.size > 0) {
      console.log('[LiveChat] Stopping AI speech and muting - stopping', this.sources.size, 'audio sources');
      // Set flag to ignore any in-flight audio
      this.responseCancelled = true;
      // Send cancel to backend FIRST to stop Azure from generating more audio
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('[LiveChat] Sending response.cancel to backend');
        this.ws.send(JSON.stringify({ type: 'response.cancel' }));
      }
      // Stop all audio sources - collect first then clear to avoid mutation during iteration
      const sourcesToStop = Array.from(this.sources);
      this.sources.clear();
      for (const source of sourcesToStop) {
        try {
          source.stop();
          console.log('[LiveChat] Stopped audio source');
        } catch (e) {
          console.log('[LiveChat] Error stopping source:', e);
        }
      }
      this.nextStartTime = 0;
      this.isAiSpeaking = false;
      this.aiStoppedSpeakingTime = Date.now();
      this.hasRawMarkdownForCurrentResponse = false;
      // Set muted state so icon changes
      this.isMuted = true;
      this.outputNode.gain.value = 0;
      // Finalize any streaming AI message
      if (this.currentAiMessageIndex >= 0 && this.currentAiMessageIndex < this.history.length) {
        this.history[this.currentAiMessageIndex].isStreaming = false;
        this.currentAiMessageIndex = -1;
      }
      this.isThinking = false;
      // Resume mic capture now that AI stopped
      this.resumeMicCapture();
      return;
    }

    // AI is not speaking - toggle mute for future audio
    this.isMuted = !this.isMuted;
    this.outputNode.gain.value = this.isMuted ? 0 : 1;
    console.log('[LiveChat] Audio muted:', this.isMuted);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect when userProfile changes and send updated config to backend
    if (changes['userProfile'] && !changes['userProfile'].firstChange) {
      const newProfile = changes['userProfile'].currentValue;
      const oldProfile = changes['userProfile'].previousValue;
      console.log('[LiveChat] User profile changed from', oldProfile, 'to', newProfile);

      // Send updated profile to backend if WebSocket is connected
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const configMsg = {
          type: 'config',
          voice: this.selectedVoice,
          profile: newProfile
        };
        console.log('[LiveChat] Sending profile update to backend:', configMsg);
        this.ws.send(JSON.stringify(configMsg));
      }
    }

    // Auto-start microphone when dialog opens via isOpen input binding
    // But only if speaker is not muted and AI is not currently speaking (to prevent echo)
    if (changes['isOpen'] && changes['isOpen'].currentValue === true && !changes['isOpen'].previousValue) {
      // Small delay to allow UI to render and audio contexts to be ready
      setTimeout(async () => {
        if (this.isMuted) {
          console.log('[LiveChat] Speaker is muted, not auto-starting microphone on isOpen change');
          return;
        }
        if (this.isOpen && !this.isRecording && !this.isAiSpeaking && this.sources.size === 0) {
          console.log('[LiveChat] Auto-starting microphone on isOpen change');
          await this.onVoiceInput();
        } else if (this.isAiSpeaking || this.sources.size > 0) {
          // AI is speaking, set flag so mic starts after AI finishes
          console.log('[LiveChat] AI is speaking, will start mic after AI finishes');
          this.wasRecordingBeforeAiSpoke = true;
        }
      }, 300);
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private cleanup(): void {
    this.pendingNavigation = null; // Clear any pending navigation
    this.stopRecording();
    this.stopAudioPlayback();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.wsReady = false;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
      this.dataChannel = null;
      this.rtcReady = false;
    }
    if (this.recognition) {
      try { this.recognition.stop(); } catch (e) { }
    }
  }

  private initSpeechRecognition(): void {
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (SpeechRecognitionAPI) {
      this.recognition = new SpeechRecognitionAPI();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US';

      // Auto-restart on end (continuous mode may still stop)
      this.recognition.onend = () => {
        console.log('[SpeechRecognition] Ended');

        // Finalize any current user message when speech recognition ends
        // This ensures the bubble is marked complete before starting fresh
        if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
          const currentMsg = this.history[this.currentUserMessageIndex];
          if (currentMsg.message && currentMsg.message.trim()) {
            currentMsg.isStreaming = false;
            console.log('[SpeechRecognition] Finalized user message on end:', currentMsg.message);
          }
          this.currentUserMessageIndex = -1;
        }

        // Reset phrase tracking since results array will be cleared
        this.currentPhraseStartIndex = 0;
        // Auto-restart if we're still recording (both Gemini and Azure use browser speech recognition for display)
        if (this.isOpen && this.isRecording && !this.isAiSpeaking) {
          console.log('[SpeechRecognition] Auto-restarting...');
          setTimeout(() => {
            if (this.isOpen && this.isRecording && this.recognition) {
              try { this.recognition.start(); } catch (e) { }
            }
          }, 100);
        }
      };

      this.recognition.onresult = (event: any) => {
        this.ngZone.run(() => {
          // Ignore results that come within 1500ms of AI stopping (echo/residual audio)
          // This longer window helps ensure we don't pick up any leftover AI audio
          const timeSinceAiStopped = Date.now() - this.aiStoppedSpeakingTime;
          if (this.aiStoppedSpeakingTime > 0 && timeSinceAiStopped < 1500) {
            console.log('[SpeechRecognition] Ignoring result - too soon after AI stopped:', timeSinceAiStopped, 'ms');
            // Also reset phrase tracking to ignore any accumulated results
            this.currentPhraseStartIndex = event.results.length;
            return;
          }

          // Build transcript only from current phrase (starting from currentPhraseStartIndex)
          // This prevents text from previous phrases from appearing in the current bubble
          let fullTranscript = '';
          for (let i = this.currentPhraseStartIndex; i < event.results.length; i++) {
            fullTranscript += event.results[i][0].transcript;
          }

          // Check if the last result is final (indicates end of phrase after pause)
          const lastResult = event.results[event.results.length - 1];
          const lastIsFinal = lastResult?.isFinal || false;

          console.log('[SpeechRecognition] Full transcript:', fullTranscript,
            'lastIsFinal:', lastIsFinal,
            'resultCount:', event.results.length);

          if (!fullTranscript.trim()) return;

          // Clear the saved message since we're actively receiving user input again
          this.userMessageBeforeAiSpoke = '';

          // When user is speaking, transcribe into user chat bubble
          if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
            const currentMsg = this.history[this.currentUserMessageIndex];
            currentMsg.message = fullTranscript.trim();

            // Only finalize when ALL results are final (user has truly stopped speaking)
            // On mobile, each word may become final individually, so we check if we've
            // accumulated enough pause time before creating a new bubble
            if (lastIsFinal) {
              // Don't immediately finalize - let more words accumulate
              // The bubble will be finalized when speech recognition restarts or stops
              console.log('[SpeechRecognition] Last result final, continuing to accumulate...');
            }
          } else {
            // Create new user message bubble
            this.currentUserMessageIndex = this.history.length;
            this.history.push({
              role: 'user',
              message: fullTranscript.trim(),
              isStreaming: true,
              timestamp: new Date()
            });
          }
        });
      };

      this.recognition.onerror = (event: any) => {
        console.error('[SpeechRecognition] Error:', event.error);
      };

      console.log('[SpeechRecognition] Initialized');
    }
  }

  /**
   * Restart speech recognition to clear accumulated results.
   * This is needed because SpeechRecognition accumulates all results in the event.results array,
   * and we need to start fresh for each user turn to avoid showing old results.
   */
  private restartSpeechRecognition(): void {
    if (this.recognition && this.isRecording) {
      console.log('[SpeechRecognition] Restarting to clear results...');
      try {
        this.recognition.stop();
        // The onend handler will auto-restart it after 100ms
      } catch (e) {
        console.error('[SpeechRecognition] Error stopping for restart:', e);
      }
    }
  }

  // Initialize connection based on model provider
  private async initConnection(): Promise<void> {
    // Both Gemini and Azure now use WebSocket
    await this.initWebSocket();
  }

  // Check if connection is ready based on provider
  private get isConnectionReady(): boolean {
    return this.wsReady;
  }

  // Initialize WebRTC for Azure Realtime API
  private async initWebRTC(): Promise<void> {
    console.log('[LiveChat] Initializing WebRTC for Azure...');

    try {
      // Step 1: Get ephemeral token from our backend
      const sessionResponse = await firstValueFrom(this.http.post<any>('/api/azure-realtime/session', {
        voice: this.selectedVoice
      }));

      if (!sessionResponse?.success || !sessionResponse?.client_secret?.value) {
        throw new Error('Failed to get ephemeral token');
      }

      const ephemeralKey = sessionResponse.client_secret.value;
      console.log('[LiveChat] Got ephemeral token');

      // Step 2: Create RTCPeerConnection
      this.peerConnection = new RTCPeerConnection();

      // Step 3: Set up audio element for AI responses
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      this.peerConnection.ontrack = (event) => {
        console.log('[LiveChat] Received audio track from Azure');
        audioEl.srcObject = event.streams[0];
        this.ngZone.run(() => {
          this.isAiSpeaking = true;
        });
      };

      // Step 4: Get microphone access and add track
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioTrack = this.mediaStream.getTracks()[0];
      this.peerConnection.addTrack(audioTrack, this.mediaStream);
      console.log('[LiveChat] Added microphone track');

      // Step 5: Create data channel for events (text messages, transcriptions)
      this.dataChannel = this.peerConnection.createDataChannel('oai-events');
      this.dataChannel.onopen = () => {
        console.log('[LiveChat] Data channel opened');
        this.ngZone.run(() => {
          this.rtcReady = true;
        });
      };

      this.dataChannel.onmessage = (event) => {
        this.handleAzureDataChannelMessage(event.data);
      };

      this.dataChannel.onerror = (error) => {
        console.error('[LiveChat] Data channel error:', error);
      };

      // Step 6: Create SDP offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      console.log('[LiveChat] Created SDP offer');

      // Step 7: Send SDP to Azure via our backend proxy
      const sdpResponse = await firstValueFrom(this.http.post('/api/azure-realtime/sdp', {
        sdp: offer.sdp,
        ephemeralKey: ephemeralKey
      }, {
        responseType: 'text'
      }));

      // Step 8: Set remote description (SDP answer from Azure)
      await this.peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: sdpResponse as string
      });
      console.log('[LiveChat] WebRTC connection established with Azure');

      // Connection state handling
      this.peerConnection.onconnectionstatechange = () => {
        console.log('[LiveChat] Connection state:', this.peerConnection?.connectionState);
        if (this.peerConnection?.connectionState === 'disconnected' ||
          this.peerConnection?.connectionState === 'failed') {
          this.resumeMicCapture();
          this.ngZone.run(() => {
            this.rtcReady = false;
            this.isAiSpeaking = false;
            this.aiStoppedSpeakingTime = Date.now();
            this.checkPendingVoiceChange();
          });
        }
      };

    } catch (error) {
      console.error('[LiveChat] Error initializing WebRTC:', error);
      throw error;
    }
  }

  // Handle messages from Azure data channel
  private handleAzureDataChannelMessage(data: string): void {
    try {
      const msg = JSON.parse(data);
      console.log('[LiveChat] Azure event:', msg.type);

      this.ngZone.run(() => {
        switch (msg.type) {
          case 'text':
            // AI responding with text (HTML/Markdown) - PREFER THIS over audio transcript
            if (this.currentAiMessageIndex < 0) {
              this.currentAiMessageIndex = this.history.length;
              this.history.push({
                role: 'model',
                message: '',
                isStreaming: true,
                timestamp: new Date(),
                avatar: this.aiAvatarSrc
              });
              this.isThinking = false;
            }
            if (msg.text && this.currentAiMessageIndex >= 0) {
              this.appendAssistantTextChunk(this.currentAiMessageIndex, msg.text);

              // Check for Markdown table syntax or HTML tables to trigger wide mode
              if (!this.isWideMode && (
                msg.text.includes('|') ||
                msg.text.includes('<table') ||
                this.history[this.currentAiMessageIndex].message.includes('|') ||
                this.history[this.currentAiMessageIndex].message.includes('<table')
              )) {
                this.isWideMode = true;
              }
            }
            break;

          case 'outputTranscription':
            // Ignore audio transcript for message content to prevent duplicate/mangled text
            // We rely on response.text.delta (type: 'text') for the visual content
            break;

          case 'conversation.item.input_audio_transcription.completed':
            // User's speech was transcribed
            if (msg.transcript) {
              // Update or create user message
              if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
                this.history[this.currentUserMessageIndex].message = msg.transcript;
                this.history[this.currentUserMessageIndex].isStreaming = false;
              } else {
                this.history.push({
                  role: 'user',
                  message: msg.transcript,
                  timestamp: new Date()
                });
              }
              this.currentUserMessageIndex = -1;
            }
            break;

          case 'response.done':
            // AI finished responding
            if (this.currentAiMessageIndex >= 0 && this.currentAiMessageIndex < this.history.length) {
              this.history[this.currentAiMessageIndex].isStreaming = false;
              this.currentAiMessageIndex = -1;
            }
            this.isAiSpeaking = false;
            this.aiStoppedSpeakingTime = Date.now();
            this.isThinking = false;
            // Resume mic capture now that AI finished
            this.resumeMicCapture();
            this.checkPendingVoiceChange();
            // Restart recognition to clear any accumulated results from AI speech
            this.restartSpeechRecognition();
            break;

          case 'input_audio_buffer.speech_started':
          case 'speechStarted':
            // User started speaking - reset cancellation flag for new response cycle
            this.responseCancelled = false;
            if (this.currentUserMessageIndex < 0) {
              this.currentUserMessageIndex = this.history.length;
              this.history.push({
                role: 'user',
                message: '...',
                isStreaming: true,
                timestamp: new Date()
              });
            }
            break;

          case 'input_audio_buffer.speech_stopped':
          case 'speechStopped':
            // User stopped speaking - show thinking animation while waiting for AI response
            if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
              this.history[this.currentUserMessageIndex].isStreaming = false;
              this.currentUserMessageIndex = -1;  // Reset so next speech creates new bubble
            }
            // Restart speech recognition to clear accumulated results
            this.restartSpeechRecognition();
            this.isThinking = true;  // Show thinking animation while AI processes
            break;

          case 'error':
            console.error('[LiveChat] Azure error:', msg.error);
            break;
        }
      });
    } catch (e) {
      console.error('[LiveChat] Error parsing Azure message:', e);
    }
  }

  // Send message via WebSocket
  private sendMessage(msg: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private appendAssistantTextChunk(messageIndex: number, chunk: string, insertSpaceBetweenChunks = false): void {
    if (!chunk || messageIndex < 0 || messageIndex >= this.history.length) return;

    const currentMessage = this.history[messageIndex];
    const existingText = currentMessage.message || '';
    const separator = insertSpaceBetweenChunks && this.shouldInsertStreamingSpace(existingText, chunk) ? ' ' : '';
    currentMessage.message = this.normalizeAssistantPunctuation(`${existingText}${separator}${chunk}`);
  }

  private shouldInsertStreamingSpace(existingText: string, chunk: string): boolean {
    if (!existingText || /\s$/.test(existingText) || /^\s/.test(chunk)) return false;

    const nextText = chunk.trimStart();
    if (!nextText) return false;

    // Azure often sends punctuation as a separate delta; do not turn "." into " .".
    if (/^[.,!?;:%\]\)}'"”’]/.test(nextText)) return false;
    if (/[([{“‘]$/.test(existingText)) return false;

    return true;
  }

  private normalizeAssistantPunctuation(text: string): string {
    return text
      .replace(/\s+([.,!?;:%])/g, '$1')
      .replace(/\b([Ss][Kk][Uu])\s*-\s*(\d+)\b/g, (_match: string, prefix: string, skuNumber: string) => `${prefix.toUpperCase()}-${skuNumber}`)
      .replace(/[ \t]*—[ \t]*/g, ' — ')
      .replace(/[ \t]{2,}/g, ' ');
  }

  private initWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Connect via the same host/port as the page, using the proxy
      // The dev server proxies /api/* to the backend (see proxy.conf.json)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

      // Use appropriate WebSocket endpoint based on provider
      const wsPath = this.modelProvider === 'azure' ? '/api/azure-live' : '/api/gemini-live';
      const wsUrl = `${protocol}//${window.location.host}${wsPath}`;

      console.log(`[LiveChat] Connecting to ${this.modelProvider} WebSocket:`, wsUrl);

      this.ws = new WebSocket(wsUrl);
      const socket = this.ws;

      socket.onopen = () => {
        console.log(`[LiveChat] WebSocket connected to ${this.modelProvider}`);
        // Send initial config with selected voice and user profile
        socket.send(JSON.stringify({
          type: 'config',
          voice: this.selectedVoice,
          profile: this.userProfile
        }));
      };

      socket.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'ready') {
            console.log('[LiveChat] Session ready with voice:', msg.voice);
            this.wsReady = true;
            resolve();
          } else if (msg.type === 'audio') {
            // Skip audio if response was cancelled by user interrupt
            // But if this is the start of a NEW response (no current AI message), reset the flag
            if (this.responseCancelled) {
              if (this.currentAiMessageIndex < 0) {
                // This is a new response - reset the flag and continue
                console.log('[LiveChat] New response detected, resetting responseCancelled flag');
                this.responseCancelled = false;
              } else {
                console.log('[LiveChat] Skipping audio - response was cancelled, currentAiMessageIndex:', this.currentAiMessageIndex);
                return;
              }
            }
            // Handle audio response
            console.log('[LiveChat] Audio received, length:', msg.data?.length, 'responseCancelled:', this.responseCancelled);

            this.nextStartTime = Math.max(
              this.nextStartTime,
              this.outputAudioContext.currentTime
            );

            const audioBuffer = await decodeAudioData(
              decode(msg.data),
              this.outputAudioContext,
              24000,
              1
            );

            const source = this.outputAudioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(this.outputNode);
            source.addEventListener('ended', () => {
              this.sources.delete(source);
              console.log('[LiveChat] Audio source ended, remaining sources:', this.sources.size);
              this.ngZone.run(() => {
                if (this.sources.size === 0) {
                  console.log('[LiveChat] All audio sources finished playing');
                  this.isAiSpeaking = false;
                  this.aiStoppedSpeakingTime = Date.now();
                  // Resume mic capture now that AI finished speaking
                  this.resumeMicCapture();
                  // Restart speech recognition now that AI is done speaking (both Gemini and Azure)
                  // Add delay to avoid picking up echo from speakers (1 second)
                  if (this.isOpen && this.isRecording && this.recognition) {
                    console.log('[SpeechRecognition] AI done speaking, restarting after delay...');
                    // Reset phrase tracking for fresh start
                    this.currentPhraseStartIndex = 0;
                    setTimeout(() => {
                      if (this.isOpen && this.isRecording && this.recognition && !this.isAiSpeaking) {
                        try { this.recognition.start(); } catch (e) { }
                      }
                    }, 1000);
                  }
                  // Apply any pending voice change
                  this.checkPendingVoiceChange();
                  // Execute any pending navigation (with 1 second delay)
                  this.checkPendingNavigation();
                }
              });
            });

            source.start(this.nextStartTime);
            this.nextStartTime += audioBuffer.duration;
            this.sources.add(source);

            this.ngZone.run(() => {
              // Create AI message bubble if not already created
              if (this.currentAiMessageIndex < 0) {
                // Finalize any streaming user message first
                if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
                  this.history[this.currentUserMessageIndex].isStreaming = false;
                  this.currentUserMessageIndex = -1;
                }

                // Stop speech recognition while AI is speaking
                if (this.recognition) {
                  try { this.recognition.stop(); } catch (e) { }
                }

                // Create new AI message
                this.currentAiMessageIndex = this.history.length;
                this.history.push({
                  role: 'model',
                  message: '',
                  isStreaming: true,
                  timestamp: new Date(),
                  avatar: this.aiAvatarSrc
                });
              }

              this.isAiSpeaking = true;
              this.isThinking = false;
            });
          } else if (msg.type === 'text') {
            // Handle text deltas from the backend WebSocket.
            // Azure/Gemini audio paths may also send outputTranscription, but tool/API
            // responses can arrive as text and should still create a chat bubble.
            console.log('[LiveChat] Text received:', msg.text);

            this.ngZone.run(() => {
              if (this.currentAiMessageIndex < 0) {
                if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
                  this.history[this.currentUserMessageIndex].isStreaming = false;
                  this.currentUserMessageIndex = -1;
                }

                this.currentAiMessageIndex = this.history.length;
                this.history.push({
                  role: 'model',
                  message: '',
                  isStreaming: true,
                  timestamp: new Date(),
                  avatar: this.aiAvatarSrc
                });
                this.isThinking = false;
              }

              if (msg.text && this.currentAiMessageIndex >= 0 && this.currentAiMessageIndex < this.history.length) {
                this.appendAssistantTextChunk(this.currentAiMessageIndex, msg.text);

                if (!this.isWideMode && (
                  msg.text.includes('|') ||
                  msg.text.includes('<table') ||
                  this.history[this.currentAiMessageIndex].message.includes('|') ||
                  this.history[this.currentAiMessageIndex].message.includes('<table')
                )) {
                  this.isWideMode = true;
                }
              }
            });
          } else if (msg.type === 'outputTranscription') {
            // Handle AI speech transcription
            console.log('[LiveChat] Output transcription:', msg.text);

            this.ngZone.run(() => {
              // If we have raw markdown displayed, create a NEW separate bubble for the verbal transcription
              // (don't append to the markdown bubble as it would mangle table formatting)
              if (this.hasRawMarkdownForCurrentResponse && this.currentAiMessageIndex >= 0) {
                // Check if we need to create a new bubble for verbal transcription
                const currentMsg = this.history[this.currentAiMessageIndex];
                if (currentMsg && currentMsg.message.includes('|')) {
                  // Current message contains table, create new bubble for verbal text
                  console.log('[LiveChat] Creating new bubble for verbal transcription after table');
                  this.currentAiMessageIndex = this.history.length;
                  this.history.push({
                    role: 'model',
                    message: '',
                    isStreaming: true,
                    timestamp: new Date(),
                    avatar: this.aiAvatarSrc
                  });
                }
              }

              // Create AI message bubble if it doesn't exist yet
              if (this.currentAiMessageIndex < 0) {
                this.isAiSpeaking = true;

                // Finalize any current user message before starting AI response
                if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
                  this.history[this.currentUserMessageIndex].isStreaming = false;
                  this.currentUserMessageIndex = -1;
                }

                if (this.recognition) {
                  try { this.recognition.stop(); } catch (e) { }
                }

                this.currentAiMessageIndex = this.history.length;
                this.history.push({
                  role: 'model',
                  message: '',
                  isStreaming: true,
                  timestamp: new Date(),
                  avatar: this.aiAvatarSrc
                });
                this.isThinking = false;
              }

              if (this.currentAiMessageIndex >= 0 && this.currentAiMessageIndex < this.history.length) {
                this.appendAssistantTextChunk(this.currentAiMessageIndex, msg.text, true);
              }

              this.isAiSpeaking = true;
            });
          } else if (msg.type === 'interrupted') {
            console.log('[LiveChat] Interrupted - stopping', this.sources.size, 'audio sources');
            // Stop all audio sources - collect first then clear to avoid mutation during iteration
            const sourcesToStop = Array.from(this.sources);
            this.sources.clear();
            for (const source of sourcesToStop) {
              try { source.stop(); } catch (e) { }
            }
            this.nextStartTime = 0;
            // Resume mic capture now that AI is interrupted
            this.resumeMicCapture();
            this.ngZone.run(() => {
              this.isAiSpeaking = false;
              this.aiStoppedSpeakingTime = Date.now();
              // Reset raw markdown flag
              this.hasRawMarkdownForCurrentResponse = false;
              if (this.currentAiMessageIndex >= 0 && this.currentAiMessageIndex < this.history.length) {
                const aiMsg = this.history[this.currentAiMessageIndex];
                if (!aiMsg.message) {
                  aiMsg.message = '(interrupted)';
                }
                aiMsg.isStreaming = false;
                this.currentAiMessageIndex = -1;
              }
              // Restart speech recognition since AI was interrupted (with delay to avoid echo)
              if (this.isOpen && this.isRecording && this.recognition) {
                console.log('[SpeechRecognition] AI interrupted, restarting after delay...');
                this.currentPhraseStartIndex = 0;
                setTimeout(() => {
                  if (this.isOpen && this.isRecording && this.recognition && !this.isAiSpeaking) {
                    try { this.recognition.start(); } catch (e) { }
                  }
                }, 1000);
              }
              this.checkPendingVoiceChange();
              // Clear any pending navigation if AI was interrupted
              this.pendingNavigation = null;
            });
          } else if (msg.type === 'turnComplete') {
            console.log('[LiveChat] Turn complete received');
            this.ngZone.run(() => {
              this.isThinking = false;
              // Reset raw markdown flag for next response
              this.hasRawMarkdownForCurrentResponse = false;

              if (this.currentAiMessageIndex >= 0 && this.currentAiMessageIndex < this.history.length) {
                const aiMsg = this.history[this.currentAiMessageIndex];
                console.log('[LiveChat] Setting AI message isStreaming to false, currentAiMessageIndex:', this.currentAiMessageIndex);
                aiMsg.isStreaming = false;

                // If message is empty (e.g. silent tool call), remove the bubble
                if (!aiMsg.message || !aiMsg.message.trim()) {
                  console.log('[LiveChat] Removing empty AI message bubble');
                  this.history.splice(this.currentAiMessageIndex, 1);
                }

                this.currentAiMessageIndex = -1;
              } else {
                console.log('[LiveChat] turnComplete: no current AI message to finalize, currentAiMessageIndex:', this.currentAiMessageIndex);
              }
              // Restart speech recognition for next user input (with delay to avoid echo)
              if (this.isOpen && this.isRecording && this.recognition && !this.isAiSpeaking) {
                console.log('[SpeechRecognition] Turn complete, restarting after delay...');
                this.currentPhraseStartIndex = 0;
                setTimeout(() => {
                  if (this.isOpen && this.isRecording && this.recognition && !this.isAiSpeaking) {
                    try { this.recognition.start(); } catch (e) { }
                  }
                }, 1000);
              }
            });
          } else if (msg.type === 'inputTranscriptionDelta') {
            // Streaming user speech transcription (from Azure) - stream words as they come
            // Suppress if AI is speaking to prevent echo from speakers being transcribed as user input
            // Also suppress if audio sources are still playing or AI just stopped speaking recently
            const timeSinceAiStopped = Date.now() - (this.aiStoppedSpeakingTime || 0);
            const recentlyStopped = timeSinceAiStopped < 2000; // 2 second buffer after AI stops
            if (this.isAiSpeaking || this.hasRawMarkdownForCurrentResponse || this.sources.size > 0 || recentlyStopped) {
              console.log('[LiveChat] Suppressing input transcription delta - AI speaking:', this.isAiSpeaking, 'sources:', this.sources.size, 'recentlyStopped:', recentlyStopped);
              return;
            }
            console.log('[LiveChat] Input transcription delta:', msg.text);
            this.ngZone.run(() => {
              // Create user message bubble if it doesn't exist
              if (this.currentUserMessageIndex < 0) {
                this.currentUserMessageIndex = this.history.length;
                this.history.push({
                  role: 'user',
                  message: '',
                  isStreaming: true,
                  timestamp: new Date()
                });
              }
              // Append the delta text to the user message
              if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
                this.history[this.currentUserMessageIndex].message += msg.text;
              }
            });
          } else if (msg.type === 'inputTranscription') {
            // User's speech transcription complete (from Azure)
            // This is the final/authoritative transcription from Azure's Whisper model
            // We use browser speech recognition for real-time display, but Azure's for final text
            // Suppress if AI is speaking to prevent echo from speakers being transcribed as user input
            const timeSinceAiStopped = Date.now() - (this.aiStoppedSpeakingTime || 0);
            const recentlyStopped = timeSinceAiStopped < 2000; // 2 second buffer after AI stops
            if (this.isAiSpeaking || this.hasRawMarkdownForCurrentResponse || this.sources.size > 0 || recentlyStopped) {
              console.log('[LiveChat] Suppressing input transcription - AI speaking:', this.isAiSpeaking, 'sources:', this.sources.size, 'recentlyStopped:', recentlyStopped);
              return;
            }
            console.log('[LiveChat] Input transcription complete (Azure):', msg.text);
            this.ngZone.run(() => {
              // Find the most recent user message that's streaming or was recently created
              // and update it with Azure's final transcription
              const lastUserMsgIdx = this.history.map((m, i) => m.role === 'user' ? i : -1).filter(i => i >= 0).pop();
              if (lastUserMsgIdx !== undefined && this.history[lastUserMsgIdx]) {
                // Update with Azure's transcription (more accurate than browser's)
                this.history[lastUserMsgIdx].message = msg.text;
                this.history[lastUserMsgIdx].isStreaming = false;
              }
              // Reset currentUserMessageIndex since this message is complete
              this.currentUserMessageIndex = -1;
            });
          } else if (msg.type === 'speechStarted') {
            // User started speaking (Azure)
            // Suppress if AI is speaking to prevent echo from speakers being detected as user speech
            const timeSinceAiStopped = Date.now() - (this.aiStoppedSpeakingTime || 0);
            const recentlyStopped = timeSinceAiStopped < 2000; // 2 second buffer after AI stops
            if (this.isAiSpeaking || this.hasRawMarkdownForCurrentResponse || this.sources.size > 0 || recentlyStopped) {
              console.log('[LiveChat] Suppressing speech started - AI speaking:', this.isAiSpeaking, 'sources:', this.sources.size, 'recentlyStopped:', recentlyStopped);
              return;
            }
            console.log('[LiveChat] User speech started');
            this.ngZone.run(() => {
              if (this.currentUserMessageIndex < 0) {
                this.currentUserMessageIndex = this.history.length;
                this.history.push({
                  role: 'user',
                  message: '',
                  isStreaming: true,
                  timestamp: new Date()
                });
              }
            });
          } else if (msg.type === 'speechStopped') {
            // User stopped speaking (Azure) - show thinking animation while waiting for AI
            console.log('[LiveChat] User speech stopped, showing thinking animation');
            this.ngZone.run(() => {
              // Stop streaming indicator on user message
              if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
                this.history[this.currentUserMessageIndex].isStreaming = false;
              }
              // Show thinking animation while AI processes
              this.isThinking = true;
            });
          } else if (msg.type === 'rawMarkdown') {
            // Raw markdown from backend (e.g., search results with tables)
            // Display directly without relying on transcribed speech
            console.log('[LiveChat] Raw markdown received, length:', msg.text?.length);
            // Pause mic capture immediately to prevent echo
            this.pauseMicCapture();
            this.ngZone.run(() => {
              // Set flag to ignore subsequent transcription for this response
              this.hasRawMarkdownForCurrentResponse = true;
              // Also set isAiSpeaking early since AI will verbalize after showing results
              // This prevents audio capture before the actual speech starts
              this.isAiSpeaking = true;

              // Create or update AI message with raw markdown
              if (this.currentAiMessageIndex < 0) {
                this.currentAiMessageIndex = this.history.length;
                this.history.push({
                  role: 'model',
                  message: msg.text || '',
                  isStreaming: true,
                  timestamp: new Date(),
                  avatar: this.aiAvatarSrc
                });
                this.isThinking = false;
              } else {
                // Replace any transcribed content with raw markdown
                this.history[this.currentAiMessageIndex].message = msg.text || '';
              }
              // Trigger wide mode for tables
              if (msg.text?.includes('|')) {
                this.isWideMode = true;
              }
            });
          } else if (msg.type === 'toolSummary') {
            // Tool summary text - display as a separate assistant chat bubble
            console.log('[LiveChat] Tool summary received:', msg.text);
            this.ngZone.run(() => {
              // Create a new assistant message bubble for the summary
              this.history.push({
                role: 'model',
                message: msg.text || '',
                isStreaming: false,
                timestamp: new Date(),
                avatar: this.aiAvatarSrc
              });
              this.isThinking = false;
            });
          } else if (msg.type === 'aiWillSpeak') {
            // Backend signals that AI is about to speak - pause mic to prevent echo
            console.log('[LiveChat] AI will speak - pausing mic capture');
            this.pauseMicCapture();
            this.ngZone.run(() => {
              this.isAiSpeaking = true;
            });
          } else if (msg.type === 'navigate') {
            // Handle navigation request from backend
            console.log('[LiveChat] Navigation request:', msg.url);
            if (msg.url) {
              // If AI is speaking, queue the navigation for after audio finishes
              if (this.isAiSpeaking || this.sources.size > 0) {
                console.log('[LiveChat] AI is speaking, queuing navigation for:', msg.url);
                this.pendingNavigation = msg.url;
              } else {
                // AI finished speaking, navigate after delay
                console.log('[LiveChat] Scheduling navigation to:', msg.url);
                setTimeout(() => {
                  this.performNavigation(msg.url);
                }, 250);
              }
            }
          } else if (msg.type === 'error') {
            console.error('[LiveChat] Error:', msg.message);
          } else if (msg.type === 'closed') {
            console.log('[LiveChat] Session closed:', msg.reason);
            this.wsReady = false;
          }
        } catch (error) {
          console.error('[LiveChat] Error processing message:', error);
        }
      };

      socket.onerror = (error) => {
        console.error('[LiveChat] WebSocket error:', error);
        reject(error);
      };

      socket.onclose = (event) => {
        console.log('[LiveChat] WebSocket closed:', event.reason);
        if (this.ws === socket) {
          this.ws = null;
          this.wsReady = false;
        }
      };
    });
  }

  private stopAudioPlayback(): void {
    // Stop all audio sources - collect first then clear to avoid mutation during iteration
    const sourcesToStop = Array.from(this.sources);
    this.sources.clear();
    for (const source of sourcesToStop) {
      try { source.stop(); } catch (e) { }
    }
    this.nextStartTime = 0;
    this.isAiSpeaking = false;
    this.aiStoppedSpeakingTime = Date.now();
    this.checkPendingVoiceChange();
    this.checkPendingNavigation();
  }

  private async startRecording(): Promise<void> {
    if (this.isRecording) return;

    // Resume audio contexts (required after user gesture)
    await this.inputAudioContext.resume();
    await this.outputAudioContext.resume();

    // Reset playback timing
    this.nextStartTime = this.outputAudioContext.currentTime;

    // Initialize connection if needed
    if (!this.isConnectionReady) {
      await this.initConnection();
    }

    console.log('[Voice] Starting recording...');

    try {
      // Both Gemini and Azure now use WebSocket - manually capture and send audio
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      console.log('[Voice] Microphone access granted');

      // Reset speech recognition tracking
      this.currentPhraseStartIndex = 0;

      this.sourceNode = this.inputAudioContext.createMediaStreamSource(this.mediaStream);
      this.sourceNode.connect(this.inputNode);

      const bufferSize = 256;
      this.scriptProcessorNode = this.inputAudioContext.createScriptProcessor(bufferSize, 1, 1);

      this.scriptProcessorNode.onaudioprocess = (audioProcessingEvent) => {
        if (!this.isRecording || !this.ws || !this.wsReady) return;

        // Don't send audio while AI is speaking or has recently spoken (prevents feedback loop)
        // Use multiple checks to be aggressive about preventing echo
        const timeSinceAiStopped = Date.now() - (this.aiStoppedSpeakingTime || 0);
        const recentlyStopped = timeSinceAiStopped < 2000; // 2 second buffer after AI stops
        if (this.isAiSpeaking || this.sources.size > 0 || this.hasRawMarkdownForCurrentResponse || recentlyStopped) {
          return; // Don't send audio - AI is speaking or just finished
        }

        const inputBuffer = audioProcessingEvent.inputBuffer;
        const pcmData = inputBuffer.getChannelData(0);

        // Send audio as JSON message
        const blob = createBlob(pcmData);
        this.ws.send(JSON.stringify({ type: 'audio', data: blob.data, mimeType: blob.mimeType }));
      };

      this.sourceNode.connect(this.scriptProcessorNode);
      this.scriptProcessorNode.connect(this.inputAudioContext.destination);

      // Start speech recognition for local transcription display (both Gemini and Azure)
      if (this.recognition) {
        try { this.recognition.start(); } catch (e) { }
      }

      this.isRecording = true;
      this.isThinking = true;
      console.log('[Voice] Recording started');
    } catch (err: any) {
      console.error('[Voice] Error:', err);
      this.stopRecording();
    }
  }

  private stopRecording(): void {
    if (!this.isRecording && !this.mediaStream) return;

    console.log('[Voice] Stopping recording');
    this.isRecording = false;

    if (this.recognition) {
      try { this.recognition.stop(); } catch (e) { }
    }

    if (this.scriptProcessorNode) {
      this.scriptProcessorNode.disconnect();
      this.scriptProcessorNode = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Finalize any streaming user message
    if (this.currentUserMessageIndex >= 0 && this.currentUserMessageIndex < this.history.length) {
      const msg = this.history[this.currentUserMessageIndex];

      // If we saved the user message before AI started speaking, restore it
      // This prevents AI's speech from contaminating the user's message
      if (this.userMessageBeforeAiSpoke) {
        msg.message = this.userMessageBeforeAiSpoke;
        this.userMessageBeforeAiSpoke = '';
      }

      if (!msg.message) {
        this.history.splice(this.currentUserMessageIndex, 1);
      } else {
        msg.isStreaming = false;
      }
      this.currentUserMessageIndex = -1;
    }

    // Reset speech recognition tracking
    this.currentPhraseStartIndex = 0;
    this.userMessageBeforeAiSpoke = '';
  }

  /**
   * Stop mic capture while AI is speaking (to prevent echo).
   * This fully stops the recording as if user clicked the mic button.
   */
  private pauseMicCapture(): void {
    if (this.isRecording) {
      this.wasRecordingBeforeAiSpoke = true;
      this.stopRecording();
      console.log('[Voice] Mic stopped (AI speaking)');
    }
  }

  /**
   * Resume mic capture after AI finishes speaking.
   * This restarts recording if it was on before AI started speaking.
   */
  private resumeMicCapture(): void {
    // Don't resume if dialog is closed
    if (!this.isOpen) {
      this.wasRecordingBeforeAiSpoke = false;
      return;
    }
    // Don't auto-resume mic if speaker is muted
    if (this.isMuted) {
      this.wasRecordingBeforeAiSpoke = false;
      console.log('[Voice] Not resuming mic - speaker is muted');
      return;
    }
    if (this.wasRecordingBeforeAiSpoke && !this.isRecording) {
      this.wasRecordingBeforeAiSpoke = false;
      console.log('[Voice] Restarting mic (AI finished)');
      // Small delay to ensure audio context is ready
      setTimeout(() => {
        if (this.isOpen && !this.isRecording && !this.isAiSpeaking) {
          this.startRecording();
        }
      }, 500);
    }
  }

  // Voice input handler
  async onVoiceInput(): Promise<void> {
    console.log('[LiveChat] onVoiceInput called - isRecording:', this.isRecording, 'isAiSpeaking:', this.isAiSpeaking, 'sources.size:', this.sources.size, 'responseCancelled:', this.responseCancelled);
    if (this.isRecording) {
      this.stopRecording();
    } else {
      // If AI is speaking, stop it first so user can speak
      if (this.isAiSpeaking || this.sources.size > 0) {
        console.log('[LiveChat] Stopping AI to allow user to speak - stopping', this.sources.size, 'audio sources');
        // Set flag to ignore any in-flight audio from current response
        this.responseCancelled = true;
        // Send cancel to backend FIRST to stop Azure from generating more audio
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          console.log('[LiveChat] Sending response.cancel to backend');
          this.ws.send(JSON.stringify({ type: 'response.cancel' }));
        }
        // Stop all audio sources - collect first then clear to avoid mutation during iteration
        const sourcesToStop = Array.from(this.sources);
        this.sources.clear();
        for (const source of sourcesToStop) {
          try {
            source.stop();
            console.log('[LiveChat] Stopped audio source');
          } catch (e) {
            console.log('[LiveChat] Error stopping source:', e);
          }
        }
        this.nextStartTime = 0;
        this.isAiSpeaking = false;
        this.aiStoppedSpeakingTime = Date.now();
        this.hasRawMarkdownForCurrentResponse = false;
        this.wasRecordingBeforeAiSpoke = false; // User is manually starting mic
        // Finalize any streaming AI message
        if (this.currentAiMessageIndex >= 0 && this.currentAiMessageIndex < this.history.length) {
          this.history[this.currentAiMessageIndex].isStreaming = false;
          this.currentAiMessageIndex = -1;
        }
        this.isThinking = false;
      }
      // Reset cancellation flag before starting to record - user is initiating new response cycle
      this.responseCancelled = false;
      console.log('[LiveChat] Reset responseCancelled before starting recording');
      await this.startRecording();
    }
  }

  // Track message count to detect new messages
  private lastMessageCount: number = 0;

  ngAfterViewChecked() {
    // Only track actual history messages, not the thinking indicator
    const currentMessageCount = this.history.length;

    // Only scroll to bottom when a new message bubble is added
    if (currentMessageCount > this.lastMessageCount) {
      console.log('[Scroll] New message added, scrolling to bottom. Count:', this.lastMessageCount, '->', currentMessageCount);
      this.lastMessageCount = currentMessageCount;
      const el = this.chatBody?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }

  resetChat() {
    this.history = [];
    this.inputValue = '';
    this.currentUserMessageIndex = -1;
    this.currentAiMessageIndex = -1;
    this.isThinking = false;
    this.isWideMode = false; // Reset wide mode on new chat
    this.currentPhraseStartIndex = 0;
    this.userMessageBeforeAiSpoke = '';
    this.pendingNavigation = null; // Clear any pending navigation
    this.pendingVoiceChange = null;
    this.responseCancelled = true;
    this.hasRawMarkdownForCurrentResponse = false; // Reset raw markdown flag
    this.lastMessageCount = 0;
    this.awaitingVendorOnboardConfirmation = false;
    this.pendingVendorSearchName = '';
    this.vendorOnboardRedirectInProgress = false;
    this.parsedMessageCache.clear();
    this.stopAudioPlayback();
    this.stopRecording();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.wsReady = false;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
      this.dataChannel = null;
      this.rtcReady = false;
    }
  }

  // Handle voice selection change
  async onVoiceChange(): Promise<void> {
    try {
      console.log('[LiveChat] Voice changed to:', this.selectedVoice, 'provider:', this.modelProvider, 'isAiSpeaking:', this.isAiSpeaking);

      // If AI is speaking, queue the voice change for later
      if (this.isAiSpeaking) {
        console.log('[LiveChat] AI is speaking, queuing voice change for:', this.selectedVoice);
        this.pendingVoiceChange = this.selectedVoice;
        return;
      }

      this.applyVoiceChange(this.selectedVoice);
    } catch (error) {
      console.error('[LiveChat] Error in onVoiceChange:', error);
    }
  }

  // Apply the voice change to the backend
  private applyVoiceChange(voice: string): void {
    console.log('[LiveChat] Applying voice change to:', voice);
    console.log('[LiveChat] WebSocket state - ws:', !!this.ws, 'wsReady:', this.wsReady, 'readyState:', this.ws?.readyState);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Stop any local speech recognition for Azure
      if (this.modelProvider === 'azure' && this.recognition) {
        try { this.recognition.stop(); } catch (e) { }
      }
      const configMsg = {
        type: 'config',
        voice: voice,
        profile: this.userProfile,
        reconnect: this.modelProvider === 'gemini' // Only Gemini needs reconnect flag
      };
      console.log('[LiveChat] Sending voice config:', configMsg);
      this.ws.send(JSON.stringify(configMsg));
      this.pendingVoiceChange = null;
    } else {
      console.log('[LiveChat] Cannot send voice change - WebSocket not ready');
    }
  }

  // Check and apply pending voice change when AI stops speaking
  private checkPendingVoiceChange(): void {
    if (this.pendingVoiceChange && !this.isAiSpeaking) {
      console.log('[LiveChat] Applying pending voice change:', this.pendingVoiceChange);
      this.applyVoiceChange(this.pendingVoiceChange);
    }
  }

  // Check and execute pending navigation when AI stops speaking (with 1 second delay)
  private checkPendingNavigation(): void {
    console.log('[LiveChat] checkPendingNavigation called - pendingNavigation:', this.pendingNavigation, 'isAiSpeaking:', this.isAiSpeaking, 'sources.size:', this.sources.size);
    if (this.pendingNavigation && !this.isAiSpeaking && this.sources.size === 0) {
      const url = this.pendingNavigation;
      this.pendingNavigation = null; // Clear it immediately to prevent double navigation
      console.log('[LiveChat] AI finished speaking, scheduling navigation to:', url);
      setTimeout(() => {
        this.performNavigation(url);
      }, 250);
    } else if (this.pendingNavigation) {
      console.log('[LiveChat] Navigation queued but conditions not met - waiting...');
    }
  }

  // Navigate to URL - uses Angular Router for in-app navigation, window.location for cross-app
  private performNavigation(url: string): void {
    console.log('[LiveChat] performNavigation called with:', url);
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const currentPath = window.location.pathname;

      // Detect app prefixes in URL paths
      const appPrefixes = ['/inventory-manager', '/customer-management', '/workspace', '/vendor-onboarding', '/expense-reimbursement', '/homepage'];
      const currentAppPrefix = appPrefixes.find(prefix => currentPath.startsWith(prefix)) || '';
      const targetAppPrefix = appPrefixes.find(prefix => parsedUrl.pathname.startsWith(prefix)) || '';

      console.log('[LiveChat] Current app prefix:', currentAppPrefix, 'Target app prefix:', targetAppPrefix);

      // If same app (or both root), use Angular Router for in-app navigation
      if (currentAppPrefix === targetAppPrefix) {
        // Extract path relative to app prefix
        let relativePath = parsedUrl.pathname;
        if (currentAppPrefix && relativePath.startsWith(currentAppPrefix)) {
          relativePath = relativePath.substring(currentAppPrefix.length) || '/';
        }

        // Build query params
        const queryParams: Record<string, string> = {};
        parsedUrl.searchParams.forEach((value, key) => {
          queryParams[key] = value;
        });

        console.log('[LiveChat] In-app navigation to:', relativePath, 'with params:', queryParams);
        this.ngZone.run(() => {
          this.router.navigate([relativePath], { queryParams });
        });
      } else {
        // Cross-app navigation - use full page reload
        console.log('[LiveChat] Cross-app navigation to:', url);
        window.location.href = url;
      }
    } catch (e) {
      console.error('[LiveChat] Failed to parse URL for navigation:', e);
      // Fallback to regular navigation
      window.location.href = url;
    }
  }

  async open(initialMessage?: string) {
    this.resetChat();
    this.isOpen = true;
    this.history = [];
    this.currentUserMessageIndex = -1;
    this.currentAiMessageIndex = -1;
    this.currentPhraseStartIndex = 0;
    this.userMessageBeforeAiSpoke = '';
    this.pendingNavigation = null; // Clear any pending navigation
    this.lastMessageCount = 0;
    this.parsedMessageCache.clear();
    this.vendorOnboardRedirectInProgress = false;
    this.cdr.detectChanges();
    this.focusChatInputSoon();

    if (initialMessage) {
      if (await this.tryHandleHomepageJohnVendorFlow(initialMessage)) {
        this.focusChatInputSoon();
        return;
      }
      // Start Gemini Live conversation with the initial message
      await this.startLiveConversation(initialMessage);
      this.focusChatInputSoon();
    } else if (!this.isMuted) {
      // Auto-start microphone when dialog opens without an initial message
      // But only if speaker is not muted and AI is not currently speaking (to prevent echo)
      // Small delay to allow UI to render and audio contexts to be ready
      setTimeout(async () => {
        if (this.isOpen && !this.isRecording && !this.isAiSpeaking && this.sources.size === 0 && !this.isMuted) {
          console.log('[LiveChat] Auto-starting microphone on dialog open');
          await this.onVoiceInput();
        } else if (this.isAiSpeaking || this.sources.size > 0) {
          // AI is speaking, set flag so mic starts after AI finishes
          console.log('[LiveChat] AI is speaking, will start mic after AI finishes');
          this.wasRecordingBeforeAiSpoke = true;
        }
      }, 300);
    } else {
      console.log('[LiveChat] Speaker is muted, not auto-starting microphone');
    }
  }

  private focusChatInputSoon(): void {
    setTimeout(() => {
      if (!this.isOpen) {
        return;
      }
      this.chatSearchBar?.focusInput();
    }, 0);
  }

  private async startLiveConversation(initialMessage: string): Promise<void> {
    this.responseCancelled = false;

    // Resume audio contexts (required for playback)
    await this.inputAudioContext.resume();
    await this.outputAudioContext.resume();

    // Reset playback timing
    this.nextStartTime = this.outputAudioContext.currentTime;

    // Add user message to history
    this.history.push({
      role: 'user',
      message: initialMessage,
      timestamp: new Date()
    });

    // Set thinking state (do NOT create AI bubble yet)
    this.isThinking = true;

    console.log('[LiveChat] Starting conversation via WebSocket:', initialMessage);

    // Initialize WebSocket connection if not already connected
    if (!this.isConnectionReady) {
      try {
        await this.initConnection();
      } catch (err) {
        console.error('[LiveChat] Failed to connect:', err);
        this.ngZone.run(() => {
          this.isThinking = false;
          this.history.push({
            role: 'model',
            message: 'Sorry, I could not connect to the AI service.',
            isStreaming: false,
            timestamp: new Date(),
            avatar: this.aiAvatarSrc
          });
        });
        return;
      }
    }

    // Send the initial message as text through the WebSocket
    // The response will come back through the existing WebSocket event handlers
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'text',
        text: initialMessage
      }));
      console.log('[LiveChat] Sent initial message via WebSocket');

      // Auto-start microphone after sending initial message
      // But only if speaker is not muted and AI is not currently speaking (to prevent echo)
      // Small delay to allow the connection to stabilize
      setTimeout(async () => {
        if (this.isMuted) {
          console.log('[LiveChat] Speaker is muted, not auto-starting microphone after initial message');
          return;
        }
        if (this.isOpen && !this.isRecording && !this.isAiSpeaking && this.sources.size === 0) {
          console.log('[LiveChat] Auto-starting microphone after initial message');
          await this.onVoiceInput();
        } else if (this.isAiSpeaking || this.sources.size > 0) {
          // AI is speaking, set flag so mic starts after AI finishes
          console.log('[LiveChat] AI is speaking, will start mic after AI finishes');
          this.wasRecordingBeforeAiSpoke = true;
        }
      }, 500);
    } else {
      console.error('[LiveChat] WebSocket not ready');
      this.ngZone.run(() => {
        this.history[this.currentAiMessageIndex].message = 'Sorry, the connection is not ready. Please try again.';
        this.history[this.currentAiMessageIndex].isStreaming = false;
        this.currentAiMessageIndex = -1;
      });
    }
  }

  private async startRecordingForConversation(): Promise<void> {
    if (this.isRecording) return;

    console.log('[Voice] Auto-starting microphone for conversation...');

    try {
      // Both Gemini and Azure now use WebSocket - manual audio capture
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      console.log('[Voice] Microphone access granted');

      // Reset speech recognition tracking
      this.currentPhraseStartIndex = 0;

      this.sourceNode = this.inputAudioContext.createMediaStreamSource(this.mediaStream);
      this.sourceNode.connect(this.inputNode);

      const bufferSize = 256;
      this.scriptProcessorNode = this.inputAudioContext.createScriptProcessor(bufferSize, 1, 1);

      this.scriptProcessorNode.onaudioprocess = (audioProcessingEvent) => {
        if (!this.isRecording || !this.ws || !this.wsReady) return;

        // Don't send audio while AI is speaking or has recently spoken (prevents feedback loop)
        // Use multiple checks to be aggressive about preventing echo
        const timeSinceAiStopped = Date.now() - (this.aiStoppedSpeakingTime || 0);
        const recentlyStopped = timeSinceAiStopped < 2000; // 2 second buffer after AI stops
        if (this.isAiSpeaking || this.sources.size > 0 || this.hasRawMarkdownForCurrentResponse || recentlyStopped) {
          return; // Don't send audio - AI is speaking or just finished
        }

        const inputBuffer = audioProcessingEvent.inputBuffer;
        const pcmData = inputBuffer.getChannelData(0);

        // Send audio as JSON message
        const blob = createBlob(pcmData);
        this.ws.send(JSON.stringify({ type: 'audio', data: blob.data, mimeType: blob.mimeType }));
      };

      this.sourceNode.connect(this.scriptProcessorNode);
      this.scriptProcessorNode.connect(this.inputAudioContext.destination);

      // Start speech recognition for user transcription display (both Gemini and Azure)
      if (this.recognition) {
        try { this.recognition.start(); } catch (e) { }
      }

      this.isRecording = true;
      console.log('[Voice] Recording started for conversation');
    } catch (err: any) {
      console.error('[Voice] Error starting recording:', err);
    }
  }

  close() {
    this.isOpen = false;
    this.pendingNavigation = null; // Clear any pending navigation
    this.wasRecordingBeforeAiSpoke = false; // Reset flag to prevent mic from restarting after close

    // Send cancel to backend to stop AI from generating more audio
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[LiveChat] Sending response.cancel to backend on close');
      this.ws.send(JSON.stringify({ type: 'response.cancel' }));
    }

    // Set flag to ignore any in-flight audio that arrives after close
    this.responseCancelled = true;

    this.stopAudioPlayback();
    this.stopRecording();
    this.closeDialog.emit();
  }

  onSend(message: string) {
    this.sendTextMessage(message);
    this.inputValue = '';
  }

  // Send a text message (used for typed messages in the input)
  // Uses the same WebSocket/Realtime API as voice input for consistency
  async sendTextMessage(text: string) {
    if (!text.trim()) return;

    if (await this.tryHandleHomepageJohnVendorFlow(text)) {
      return;
    }

    // Resume audio contexts
    await this.inputAudioContext.resume();
    await this.outputAudioContext.resume();

    // Ensure WebSocket is connected
    if (!this.wsReady) {
      console.log('[LiveChat] WebSocket not ready, initializing connection...');
      try {
        await this.initConnection();
      } catch (error) {
        console.error('[LiveChat] Failed to initialize connection:', error);
        this.history.push({ role: 'user', message: text, timestamp: new Date() });
        this.history.push({
          role: 'model',
          message: 'Sorry, I could not connect to the AI service.',
          isStreaming: false,
          timestamp: new Date(),
          avatar: this.aiAvatarSrc
        });
        return;
      }
    }

    this.stopAudioPlayback();

    // Reset cancellation flag for new response
    this.responseCancelled = false;

    // Add user message to history
    this.history.push({ role: 'user', message: text, timestamp: new Date() });
    this.isThinking = true;

    console.log('[LiveChat] Sending text via WebSocket:', text);

    // Send text message via WebSocket (same path as voice)
    // The backend will forward to Azure Realtime API and responses come back via the same handlers
    this.sendMessage({
      type: 'text',
      text: text
    });

    // The response will come back through the ws.onmessage handlers:
    // - outputTranscription for text
    // - audio for spoken response
    // - turnComplete when done
  }

  private async tryHandleHomepageJohnVendorFlow(text: string): Promise<boolean> {
    const raw = String(text || '').trim();
    if (!raw) return false;
    if (this.vendorOnboardRedirectInProgress) return true;

    if (this.awaitingVendorOnboardConfirmation && this.isHomepageJohnProfile()) {
      this.history.push({ role: 'user', message: raw, timestamp: new Date() });
      if (this.isAffirmative(raw)) {
        const vendorName = this.pendingVendorSearchName;
        this.vendorOnboardRedirectInProgress = true;
        this.awaitingVendorOnboardConfirmation = false;
        this.pendingVendorSearchName = '';
        this.history.push({
          role: 'model',
          message: `No match found. Opening a new vendor request for **${vendorName}** now.`,
          isStreaming: false,
          timestamp: new Date(),
          avatar: this.aiAvatarSrc
        });
        this.performNavigation(`/vendor-onboarding/vendors/request?vendorName=${encodeURIComponent(vendorName)}&fromCheck=1`);
        return true;
      }

      if (this.isNegative(raw)) {
        this.awaitingVendorOnboardConfirmation = false;
        const vendorName = this.pendingVendorSearchName;
        this.pendingVendorSearchName = '';
        this.history.push({
          role: 'model',
          message: `No problem. I won't start onboarding for **${vendorName}**.`,
          isStreaming: false,
          timestamp: new Date(),
          avatar: this.aiAvatarSrc
        });
        return true;
      }
    }

    const vendorName = this.extractHomepageJohnVendorSearchName(raw);
    if (!vendorName) return false;

    this.history.push({ role: 'user', message: raw, timestamp: new Date() });
    this.isThinking = true;

    try {
      const rows = await firstValueFrom(this.queryVendorRowsByName(vendorName));
      const matches = this.filterVendorMatches(rows, vendorName);
      this.isThinking = false;

      if (matches.length) {
        this.awaitingVendorOnboardConfirmation = false;
        this.pendingVendorSearchName = '';
        this.history.push({
          role: 'model',
          message: this.vendorResultsMarkdown(vendorName, matches),
          isStreaming: false,
          timestamp: new Date(),
          avatar: this.aiAvatarSrc
        });
      } else {
        this.awaitingVendorOnboardConfirmation = true;
        this.pendingVendorSearchName = vendorName;
        this.history.push({
          role: 'model',
          message: `I couldn't find any vendors matching **${vendorName}**. Would you like to onboard that vendor?`,
          isStreaming: false,
          timestamp: new Date(),
          avatar: this.aiAvatarSrc
        });
      }
      return true;
    } catch (error) {
      console.error('[LiveChat] Vendor search failed:', error);
      this.isThinking = false;
      this.history.push({
        role: 'model',
        message: `I couldn't search the vendor database right now for **${vendorName}**. Please try again.`,
        isStreaming: false,
        timestamp: new Date(),
        avatar: this.aiAvatarSrc
      });
      return true;
    }
  }

  private isHomepageJohnProfile(): boolean {
    const profile = String(this.userProfile || '').trim().toLowerCase();
    return profile === 'john';
  }

  private extractHomepageJohnVendorSearchName(text: string): string {
    if (!this.isHomepageJohnProfile()) return '';
    const normalized = String(text || '').trim();
    if (!normalized) return '';

    const patterns = [
      /^(?:find|search(?:\s+for)?|look\s+up)\s+vendor\s+(.+)$/i,
      /^(?:find|search(?:\s+for)?|look\s+up)\s+(.+)$/i
    ];

    for (const pattern of patterns) {
      const match = normalized.match(pattern);
      if (!match?.[1]) continue;
      const candidate = match[1].trim().replace(/^["']|["']$/g, '');
      if (candidate) return candidate;
    }

    return '';
  }

  private isAffirmative(text: string): boolean {
    return /\b(yes|y|yeah|yep|sure|ok|okay|please do|go ahead|start|onboard)\b/i.test(text);
  }

  private isNegative(text: string): boolean {
    return /\b(no|n|nope|not now|cancel|stop)\b/i.test(text);
  }

  private vendorAuthCandidates(): SnApiAuth[] {
    return this.snApi.getVendorOnboardingAuthCandidates('Purchasing Agent');
  }

  private queryVendorRowsByName(vendorName: string, idx = 0): import('rxjs').Observable<Record<string, any>[]> {
    const auth = this.vendorAuthCandidates()[idx];
    if (!auth) {
      throw new Error('Unable to authenticate to ServiceNow vendor table.');
    }

    const safeVendorName = vendorName.replace(/\^/g, '').trim();
    const query = [
      `u_nameLIKE${safeVendorName}`,
      `u_name_1LIKE${safeVendorName}`,
      `u_name_1_LIKE${safeVendorName}`,
      `u_name1LIKE${safeVendorName}`,
      `u_vendor_nameLIKE${safeVendorName}`,
      `u_vendor_name_1LIKE${safeVendorName}`
    ].join('^OR');

    return this.snApi.getTableRows<Record<string, any>>(this.vendorOnboardingTable, {
      sysparm_limit: '100',
      sysparm_display_value: 'true',
      sysparm_query: query
    }, auth).pipe(
      map((response: NowList<Record<string, any>>) => response?.result || []),
      catchError((error) => {
        if (idx >= this.vendorAuthCandidates().length - 1) {
          return throwError(() => error);
        }
        return this.queryVendorRowsByName(vendorName, idx + 1);
      })
    );
  }

  private filterVendorMatches(rows: Record<string, any>[], vendorName: string): Record<string, any>[] {
    const normalized = vendorName.toLowerCase();
    const dedupe = new Set<string>();
    return (rows || []).filter((row) => {
      const name = this.vendorNameFromRow(row);
      if (!name || !name.toLowerCase().includes(normalized)) return false;
      const sysId = this.firstVendorValue(row, ['sys_id']);
      const key = `${sysId}:${name}`.toLowerCase();
      if (dedupe.has(key)) return false;
      dedupe.add(key);
      return true;
    });
  }

  private vendorResultsMarkdown(vendorName: string, rows: Record<string, any>[]): string {
    const lines = [
      `I found ${rows.length} vendor match${rows.length === 1 ? '' : 'es'} for **${vendorName}**:`,
      '',
      '| Vendor | Country | Status | Requested By |',
      '| --- | --- | --- | --- |'
    ];

    rows.slice(0, 10).forEach((row) => {
      lines.push(
        `| ${this.escapeMarkdownCell(this.vendorNameFromRow(row) || 'Vendor')} | ${this.escapeMarkdownCell(this.firstVendorValue(row, ['u_vendor_country', 'u_country', 'u_country_code']) || '-')} | ${this.escapeMarkdownCell(this.vendorRequestStatusFromRow(row) || '-')} | ${this.escapeMarkdownCell(this.firstVendorValue(row, ['u_submitted_by', 'u_requested_by', 'sys_created_by']) || '-')} |`
      );
    });

    return lines.join('\n');
  }

  private vendorNameFromRow(row: Record<string, any>): string {
    return this.firstVendorValue(row, [
      'u_vendor_name',
      'u_vendor_name_1',
      'u_name_1',
      'u_name_1_',
      'u_name1',
      'u_vendor',
      'u_name'
    ]);
  }

  private vendorRequestStatusFromRow(row: Record<string, any>): string {
    return this.firstVendorValue(row, ['u_status', 'u_request_status']) || 'Pending Review';
  }

  private firstVendorValue(row: Record<string, any>, fields: string[]): string {
    for (const field of fields) {
      const value = this.coerceVendorValue(row?.[field]);
      if (value) return value;
    }
    return '';
  }

  private coerceVendorValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      const text = String(value).trim();
      return text && text.toLowerCase() !== 'null' && text.toLowerCase() !== 'undefined' ? text : '';
    }
    if (typeof value === 'object') {
      const display = this.coerceVendorValue((value as any)?.display_value);
      if (display) return display;
      const raw = this.coerceVendorValue((value as any)?.value);
      if (raw) return raw;
    }
    return '';
  }

  private escapeMarkdownCell(value: string): string {
    return String(value || '')
      .replace(/\|/g, '\\|')
      .replace(/\n/g, ' ')
      .trim();
  }

  /**
   * Play TTS audio for the given text using the backend TTS API
   */
  private async playTtsForText(text: string): Promise<void> {
    if (!text?.trim()) return;

    // Strip HTML tags for TTS
    const plainText = text.replace(/<[^>]*>/g, '').trim();
    if (!plainText) return;

    console.log('[LiveChat] Playing TTS for:', plainText.substring(0, 50) + '...');

    try {
      const response = await firstValueFrom(this.http.post<any>('/api/tts', { text: plainText }));

      if (response?.success && response?.audio?.data) {
        const audioBytes = decode(response.audio.data);
        const audioBuffer = await decodeAudioData(audioBytes, this.outputAudioContext, 24000, 1);

        const source = this.outputAudioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.outputNode);

        source.addEventListener('ended', () => {
          this.sources.delete(source);
          this.ngZone.run(() => {
            if (this.sources.size === 0) {
              this.isAiSpeaking = false;
              this.aiStoppedSpeakingTime = Date.now();
              // Resume mic capture now that TTS finished
              this.resumeMicCapture();
            }
          });
        });

        this.isAiSpeaking = true;
        source.start();
        this.sources.add(source);
        console.log('[LiveChat] TTS audio playback started');
      }
    } catch (error) {
      console.error('[LiveChat] TTS error:', error);
    }
  }

  /**
   * Parse message text to convert markdown and HTML to rendered content.
   * Uses marked library for markdown parsing (tables, lists, etc.)
   * and handles HTML anchor tags for clickable links.
   */
  parseMessageLinks(message: string): SafeHtml {
    if (!message) return '';

    const cached = this.parsedMessageCache.get(message);
    if (cached) return cached;

    // Convert HTML anchor tags to proper clickable links first
    // Match patterns like <A HREF="...">...</A> or <a href="...">...</a> (case insensitive)
    const linkRegex = /<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;

    let result = message.replace(linkRegex, (match, href, text) => {
      // Create a styled link
      return `<a href="${href}" class="chat-link" target="_self">${text}</a>`;
    });

    // Preprocess: Fix single-line markdown tables from AI responses
    // AI often returns tables without proper newlines
    //console.log('[ParseMessage] Before preprocessing:', result);
    result = this.preprocessMarkdownTables(result);
    //console.log('[ParseMessage] After preprocessing:', result);

    // Use marked library to parse markdown (tables, lists, bold, etc.)
    // Note: breaks must be false for tables to parse correctly
    const parsedMarkdown = marked.parse(result, {
      gfm: true,        // GitHub Flavored Markdown (includes tables)
      breaks: false,    // Must be false for table parsing to work
      async: false      // Synchronous parsing
    }) as string;

    // Check for table content to trigger wide mode
    if (!this.isWideMode && parsedMarkdown.includes('<table')) {
      this.isWideMode = true;
    }

    const safeHtml = this.sanitizer.bypassSecurityTrustHtml(parsedMarkdown);
    this.parsedMessageCache.set(message, safeHtml);
    if (this.parsedMessageCache.size > this.maxParsedMessageCacheEntries) {
      const oldestKey = this.parsedMessageCache.keys().next().value;
      if (oldestKey) {
        this.parsedMessageCache.delete(oldestKey);
      }
    }

    return safeHtml;
  }

  /**
   * Preprocess markdown text to fix single-line tables from AI responses.
   * Handles escaped newlines (\\n) from JSON and missing newlines.
   * IMPORTANT: Only applies transformations if table appears malformed.
   */
  private preprocessMarkdownTables(text: string): string {
    if (!text || !text.includes('|')) return text;

    // Pattern 0: Convert escaped newlines from JSON to actual newlines
    // AI responses often come through JSON.stringify which escapes \n to \\n
    text = text.replace(/\\n/g, '\n');

    // Pattern 0.5: Ensure blank line before table
    // Markdown tables require a blank line before them to be valid
    // If we find a line starting with |, check if previous line is blank
    const linesTemp = text.split('\n');
    let needsRebuild = false;
    for (let i = 1; i < linesTemp.length; i++) {
      const line = linesTemp[i].trim();
      const prevLine = linesTemp[i - 1].trim();
      // If current line looks like table header (|...|) and prev line is NOT empty and NOT a table line
      if (line.startsWith('|') && line.includes('|') && prevLine !== '' && !prevLine.startsWith('|')) {
        linesTemp[i] = '\n' + linesTemp[i]; // Add extra newline before this line
        needsRebuild = true;
      }
    }
    if (needsRebuild) {
      text = linesTemp.join('\n');
      console.log('[Preprocess] Added missing blank lines before table');
    }

    // Check if table is already properly formatted (has multiple lines starting with |)
    let lines = text.split('\n');
    let pipeLines = lines.filter(line => line.trim().startsWith('|'));

    // If we have 3+ lines starting with | (header, separator, data), table is likely valid
    // BUT we still need to check for joined rows (||) which can happen even in "valid" tables
    if (pipeLines.length >= 3 && !text.includes('||')) {
      console.log('[Preprocess] Table appears valid with', pipeLines.length, 'pipe lines - skipping aggressive transforms');
      return text;
    }

    console.log('[Preprocess] Table processing needed: pipeLines=', pipeLines.length, 'hasDoublePipes=', text.includes('||'));

    // Pattern 1: Double pipes "||" indicate missing newlines between rows
    if (text.includes('||')) {
      text = text.replace(/\|\|/g, '|\n|');
    }

    // Pattern 1.5: Fix wrapped content inside cells
    // Azure AI sometimes wraps cell content to new lines, breaking the table structure
    // If a line doesn't start with | but looks like it belongs to the previous table line
    const linesWrap = text.split('\n');
    let insideTable = false;
    let rebuild = false;

    for (let i = 0; i < linesWrap.length; i++) {
      const line = linesWrap[i].trim();
      if (line.startsWith('|')) {
        insideTable = true;
      } else if (insideTable && line.length > 0 && !line.startsWith('|') && i > 0) {
        // Found a NON-empty line, inside a table block, that does NOT start with |
        // This is likely wrapped content. Appending it to previous line.
        // We use <br> or space. Space is safer for Markdown.
        linesWrap[i - 1] = linesWrap[i - 1] + ' ' + linesWrap[i];
        linesWrap[i] = ''; // Clear this line
        rebuild = true;
      } else if (line === '' && insideTable) {
        // Empty line might mean end of table
        // But valid markdown tables shouldn't have empty lines INSIDE rows
        // So we just leave it. If next line starts with |, it's a new row.
      }
    }

    if (rebuild) {
      text = linesWrap.filter(l => l !== '').join('\n');
      console.log('[Preprocess] Fixed wrapped table content');
    }

    console.log('[Preprocess] Table appears malformed with only', pipeLines.length, 'pipe lines - applying fixes');

    // Only apply these fixes if the table appears to be on a single line or malformed

    // Pattern 1: Double pipes "||" indicate missing newlines between rows
    if (text.includes('||')) {
      text = text.replace(/\|\|/g, '|\n|');
    }

    // Pattern 2: Pipe followed by space and pipe " | |" with no content - likely row boundary
    text = text.replace(/\|\s+\|(?=\s*[^-|])/g, '|\n|');

    // Pattern 3: Add newline before separator rows if missing
    text = text.replace(/\|([^|\n]*)\|(\s*\|[\s:-]+\|)/g, '|$1|\n$2');

    // Pattern 4: Add newline after separator rows if missing 
    text = text.replace(/(\|[\s:-]+\|)(\s*)(\|[^-|\n])/g, '$1\n$3');

    // Pattern 5: Handle linearized tables where rows are separated by " | | " or similar
    // If a line looks like it has multiple table cells in sequence without proper structure
    // Look for patterns like "| value | | value |" which should be "| value |\n| value |"
    // This is a more aggressive fix for truly mangled single-line tables
    if (pipeLines.length <= 1) {
      // Check if we have what looks like a separator pattern (---) somewhere in the text
      if (text.includes('---')) {
        // Split on patterns that look like end-of-row followed by start-of-row
        // e.g., "| 18 | 91 | | KEP-" -> "| 18 | 91 |\n| KEP-"
        text = text.replace(/\|\s*\|\s*\|/g, '|\n|');

        // Also split before separator rows
        text = text.replace(/\|\s*([\-]+\s*\|)+/g, (match) => '\n' + match.trim());
      }
    }

    // Re-check if we now have a valid table
    lines = text.split('\n');
    pipeLines = lines.filter(line => line.trim().startsWith('|'));
    console.log('[Preprocess] After fixes:', pipeLines.length, 'pipe lines');

    return text;
  }

}
